import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, ArrowLeft, Mail, Phone, Globe, Calendar, Users, MapPin, Building2, FileText, Eye, Copy } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ViewStartup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startup, setStartup] = useState(null);
  const [stories, setStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(false);

  useEffect(() => {
    fetchStartupDetails();
    fetchStartupStories();
  }, [id]);

  const fetchStartupDetails = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/startups/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStartup(response.data.data);
    } catch (err) {
      toast.error('Failed to load startup details');
      navigate('/admin/startups');
    } finally {
      setLoading(false);
    }
  };

  const fetchStartupStories = async () => {
    try {
      setLoadingStories(true);
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/stories?startup_id=${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Stories response:', response.data); // Debug log
      setStories(response.data.data || []);
    } catch (err) {
      console.error('Failed to load stories:', err);
      toast.error('Failed to load stories');
    } finally {
      setLoadingStories(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!startup) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Startup not found</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'Invited':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'Pending':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-500/20';
    }
  };

  const copySubmissionLink = () => {
    const link = `${window.location.origin}/submit-story/${startup.story_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Submission link copied to clipboard!');
  };

  // Get industry name - check multiple possible field names
  const getIndustryName = () => {
    // Try different field variations
    if (startup.industry_name) return startup.industry_name;
    if (startup.industry) return startup.industry;
    return 'Industry not specified';
  };

  // Calculate analytics
  const publishedStories = stories.filter(s => s.status === 'Published').length;
  const draftStories = stories.filter(s => s.is_draft).length;
  const submittedStories = stories.filter(s => s.status === 'Submitted' && !s.is_draft).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/startups')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Badge className={`${getStatusColor(startup.status)} border-2`}>
          {startup.status}
        </Badge>
      </div>

      {/* Startup Overview - Name on Left */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-6">
            {startup.logo && (
              <img 
                src={startup.logo} 
                alt={startup.startup_name}
                className="w-16 h-16 object-contain rounded-lg border p-2"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold">{startup.startup_name}</h1>
              <p className="text-muted-foreground">{getIndustryName()}</p>
            </div>
          </div>
          
          {/* Quick Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {startup.founding_year && (
              <div className="text-center p-3 bg-accent/50 rounded-lg">
                <Calendar className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <p className="text-sm font-semibold">{startup.founding_year}</p>
                <p className="text-xs text-muted-foreground">Founded</p>
              </div>
            )}
            {startup.employee_count_range && (
              <div className="text-center p-3 bg-accent/50 rounded-lg">
                <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <p className="text-sm font-semibold">{startup.employee_count_range}</p>
                <p className="text-xs text-muted-foreground">Employees</p>
              </div>
            )}
            {(startup.city_name || startup.country_name) && (
              <div className="text-center p-3 bg-accent/50 rounded-lg">
                <MapPin className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <p className="text-sm font-semibold">
                  {startup.city_name || startup.country_name}
                </p>
                <p className="text-xs text-muted-foreground">Location</p>
              </div>
            )}
            <div className="text-center p-3 bg-accent/50 rounded-lg">
              <FileText className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-sm font-semibold">{stories.length}</p>
              <p className="text-xs text-muted-foreground">Total Stories</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {startup.website_url && (
              <Button size="sm" variant="outline" asChild>
                <a href={startup.website_url} target="_blank" rel="noopener noreferrer">
                  <Globe className="h-4 w-4 mr-2" />
                  Website
                </a>
              </Button>
            )}
            {startup.contact_email && (
              <Button size="sm" variant="outline" asChild>
                <a href={`mailto:${startup.contact_email}`}>
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </a>
              </Button>
            )}
            {startup.story_token && (
              <Button size="sm" variant="outline" onClick={copySubmissionLink}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Story Link
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Founders & Contact */}
        <div className="space-y-6">
          {/* Founders */}
          {startup.founders && startup.founders.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Founders</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {startup.founders.map((founder, index) => (
                  <div key={index} className="pb-3 border-b last:border-0 last:pb-0">
                    <p className="font-semibold">{founder.name || 'N/A'}</p>
                    {founder.designation && (
                      <p className="text-sm text-muted-foreground">{founder.designation}</p>
                    )}
                    {founder.email && (
                      <a 
                        href={`mailto:${founder.email}`}
                        className="text-sm text-primary hover:underline block mt-1"
                      >
                        {founder.email}
                      </a>
                    )}
                    {founder.phone && (
                      <p className="text-sm text-muted-foreground">
                        {founder.country_code} {founder.phone}
                      </p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Contact Info */}
          {(startup.contact_phone || startup.contact_email) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {startup.contact_email && (
                  <div>
                    <p className="text-muted-foreground mb-1">Email</p>
                    <a 
                      href={`mailto:${startup.contact_email}`}
                      className="text-primary hover:underline"
                    >
                      {startup.contact_email}
                    </a>
                  </div>
                )}
                {startup.contact_phone && (
                  <div>
                    <p className="text-muted-foreground mb-1">Phone</p>
                    <p>{startup.contact_country_code} {startup.contact_phone}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Analytics & Stories */}
        <div className="lg:col-span-2 space-y-6">
          {/* Analytics Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Story Analytics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-3xl font-bold text-green-700">{publishedStories}</p>
                  <p className="text-sm text-green-600 mt-1">Published</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-3xl font-bold text-yellow-700">{submittedStories}</p>
                  <p className="text-sm text-yellow-600 mt-1">Submitted</p>
                </div>
                <div className="text-center p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-3xl font-bold text-blue-700">{draftStories}</p>
                  <p className="text-sm text-blue-600 mt-1">Drafts</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stories List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">All Stories</CardTitle>
                <Badge variant="secondary">{stories.length}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {loadingStories ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : stories.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No stories published yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {stories.map((story) => (
                    <div 
                      key={story.id}
                      className="p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold mb-1 line-clamp-1">
                            {story.title || story.startup_name}
                          </h3>
                          {story.short_description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                              {story.short_description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <Badge variant="secondary" className="text-xs">
                              {story.status}
                            </Badge>
                            <span>{story.submission_type || 'Questionnaire'}</span>
                            {story.submitted_at && (
                              <span>
                                {new Date(story.submitted_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (story.slug) {
                              window.open(`/story/${story.slug}`, '_blank');
                            }
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
